import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Badge } from "react-bootstrap";
import { TbArrowsExchange } from "react-icons/tb";
import FilterComponent from "./filterComponent";
import {
  useSpinner,
  convertFromDateToUnix,
  convertToDateToUnix,
  datePreferenceUtility,
  ExpandableTable,
  paginatedPostRequestBody,
  SearchComponent,
  Spinner,
  UTCToDateTimeFormat,
} from "../../../../shared";
import {
  mergeDuplicateStoresConstants,
  statusColors,
} from "../../constants/mergeDuplicateStores.constants";
import { emptyString, NOT_APPLICABLE } from "../../constants";
import { mergeDuplicateStoresActions } from "../../redux/actions/mergeDuplicateStores.actions";

const MergeDublicateStoreLogsTable = (props) => {
  const {
    getMergeDuplicateStoresLogs,
    mergeDuplicateStoresData,
    mergeDuplicateStoresPaginationData,
  } = props;

  // Show loading while a request is running.
  const spinner = useSpinner();

  const [queryString, setQueryString] = useState(emptyString);
  const [searchString, setSearchString] = useState(emptyString);
  const [selectedSubject, setSelectedSubject] = useState(emptyString);
  const [dateState, setDateState] = useState(datePreferenceUtility());

  const createDateFrom = convertFromDateToUnix(dateState.startDate);
  const createDateTo = convertToDateToUnix(dateState.endDate);

  const getStatusTag = (status) => {
    const color = statusColors[status] || "secondary";
    return (
      <Badge bg={color} className="text-capitalize">
        {status}
      </Badge>
    );
  };

  //Expandable table headers
  const tableColumns = [
    {
      name: <span className="fw-bold">Brand Name</span>,
      selector: (row) => row.brandName,
    },
    {
      name: <span className="fw-bold">Old Store Name</span>,
      selector: (row) => row.oldStoreName,
    },
    {
      name: <span className="fw-bold">New Store Name</span>,
      selector: (row) => row.newStoreName,
    },
    {
      name: <span className="fw-bold">Records to Merge</span>,
      selector: (row) => row.totalRecordsToMerge,
    },
    {
      name: <span className="fw-bold">Records Merged</span>,
      selector: (row) => row.noOfRecordsMerged,
    },
    {
      name: <span className="fw-bold">Status</span>,
      selector: (row) => getStatusTag(row.status),
    },
    {
      name: <span className="fw-bold">Created At</span>,
      selector: (row) => UTCToDateTimeFormat(row.createdAt),
    },
  ];

  //set pagination details with request body
  const getPaginatedRequestBody = (pageNumber) => {
    return paginatedPostRequestBody(
      mergeDuplicateStoresPaginationData,
      pageNumber,
      searchString,
      {
        id: "createdAt",
        desc: true,
      },
      queryString
    );
  };

  // Handle display data on row expanding
  const expandTableRow = ({ data }) => {
    return (
      <div className="mx-5 my-3">
        <p className="mb-1">
          <strong>Message:</strong> {data.message}
        </p>
        <p className="mb-1">
          <strong>Completed At:</strong> {UTCToDateTimeFormat(data.completedAt)}
        </p>
        <p className="mb-1">
          <strong>Managers:</strong>{" "}
          {data.managers?.length > 0
            ? data.managers.join(", ")
            : NOT_APPLICABLE}
        </p>
        <p className="mb-1">
          <strong>Group:</strong>{" "}
          {data.groupName !== null ? data.groupName : NOT_APPLICABLE}
        </p>
        <p className="mb-2">
          <strong>Store Details:</strong>
        </p>
        <p className="row mt-3 mx-1">
          <div className="col-md-5 p-3 border">
            <h6 className="font-weight-semibold mb-1 fw-bold">Old Store</h6>
            <div className={"d-flex flex-column"}>
              <span className="mb-1">
                <strong>Name:</strong> {data.oldStoreName}
              </span>
              <span className="mb-1">
                <strong>ID:</strong> {data.oldStoreId}
              </span>
              <span>
                <strong>FR ID:</strong> {data.oldStoreExternalId}
              </span>
            </div>
          </div>
          <div className="col-md-2 d-flex align-items-center justify-content-center">
            <TbArrowsExchange size={25} className="text-muted" />
          </div>
          <div className="col-md-5 p-3 border">
            <h6 className="font-weight-semibold mb-1 fw-bold">New Store</h6>
            <div className={"d-flex flex-column"}>
              <span className="mb-1">
                <strong>Name:</strong> {data.newStoreName}
              </span>
              <span className="mb-1">
                <strong>ID:</strong> {data.newStoreId}
              </span>
              <span>
                <strong>FR ID:</strong> {data.newStoreExternalId}
              </span>
            </div>
          </div>
        </p>
      </div>
    );
  };

  //Get table data with pagination
  const getData = (pageNumber = null) => {
    const requestBody = getPaginatedRequestBody(pageNumber);
    getMergeDuplicateStoresLogs(requestBody);
  };

  //Get data on first render also on filter and search change
  useEffect(() => {
    if (queryString || searchString) {
      getData(0);
    }
    // eslint-disable-next-line
  }, [searchString, queryString]);

  useEffect(() => {
    let query = `(createdAt > ${createDateFrom} and createdAt <= ${createDateTo})`;

    if (selectedSubject) {
      const subjectQuery = `(status="${selectedSubject}")`;
      query = `${subjectQuery} AND ${query}`;
    }

    setQueryString(query);
    // eslint-disable-next-line
  }, [createDateFrom, createDateTo, selectedSubject]);

  return (
    <div>
      <FilterComponent
        refreshData={getData}
        dateState={dateState}
        setDateState={setDateState}
        selectedSubject={selectedSubject}
        setSelectedSubject={setSelectedSubject}
      />
      <div className="py-3">
        <div className="bg-white shadow rounded">
          <div className="d-flex justify-content-between align-items-center ps-4 pt-4">
            <h5>Merge Stores History:</h5>
            <SearchComponent
              searchString={searchString}
              setSearchString={setSearchString}
            />
          </div>
          <hr className="mx-4" />
          {spinner(
            mergeDuplicateStoresConstants.GET_MERGE_DUBLICATE_STORE_LOGS
          ) ? (
            <Spinner />
          ) : (
            <div className="px-4">
              <ExpandableTable
                perPage={[10]}
                columns={tableColumns}
                expandOnRowClicked
                data={mergeDuplicateStoresData || []}
                keyField="surveyQueues"
                handlePageChange={(pageNum) => {
                  getData(pageNum);
                }}
                expandRowRenderer={expandTableRow}
                pageNumber={mergeDuplicateStoresPaginationData.number + 1}
                totalRows={mergeDuplicateStoresPaginationData.totalElements}
                numberOfRowsPerPage={10}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  mergeDuplicateStoresPaginationData:
    state.mergeDuplicateStoresReducer.mergeDuplicateStoresPaginationData,
  mergeDuplicateStoresData:
    state.mergeDuplicateStoresReducer.mergeDuplicateStoresData,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getMergeDuplicateStoresLogs: (body) =>
    dispatch(mergeDuplicateStoresActions.getMergeDuplicateStoresLogs(body)),
});

// Mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MergeDublicateStoreLogsTable);
